import React, { useEffect, useState, useMemo } from 'react';
import { portalApi } from '../../context/ClientPortalAuthContext';
import { ArrowRight, Printer, Phone, Laptop, HardDrive, X } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// Fallback static paths if the admin hasn't uploaded a library image yet
const FALLBACK_IMAGES = {
  callwork: '/ads/callwork.webp',
  kopier: '/ads/kopier.webp',
  'macbook-pro': '/ads/macbook-pro.webp',
  'synology-nas': '/ads/synology-nas.webp',
};

function resolveAsset(slots, slotKey, kind) {
  const entry = slots?.[slotKey]?.[kind];
  if (entry?.url) {
    return `${API_BASE}${entry.url}`;
  }
  if (kind === 'image') return FALLBACK_IMAGES[slotKey];
  return null;
}

/**
 * SmartAds — conditional cross-sell banners on the client portal dashboard.
 *
 * Detects which services the client already has (MPS / UCaaS / ITaaS /
 * DRaaS) from their contracts and equipment, and renders ads only for
 * the services they don't have yet. Priority order:
 *   1. Complementary core service (telephony ↔ impression)
 *   2. ITaaS (IT maintenance)
 *   3. DRaaS (data backup)
 *
 * Brand visuals (image + logo) are managed by admins under
 * Paramètres → Bibliothèque d'images publicitaires. Logos are rendered in
 * white via CSS filter; product images are placed on the right.
 */

const CTA = {
  telephony: 'https://tu9asy65pqw.typeform.com/to/ny2j8mNP',
  printing: 'https://tu9asy65pqw.typeform.com/to/puCKWtnY',
  itaas: 'https://tu9asy65pqw.typeform.com/to/A5lSo5fp',
  draas: 'https://tu9asy65pqw.typeform.com/to/KfFiP53A',
};

// Persistence helpers for dismissing the primary ad
const DISMISS_DAYS = 14;
const dismissKey = (slot, clientId) => `smartads_dismissed_${slot}_${clientId || 'anon'}`;

function isDismissed(slot, clientId) {
  try {
    const raw = localStorage.getItem(dismissKey(slot, clientId));
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 86400 * 1000;
  } catch (_) {
    return false;
  }
}

function setDismissed(slot, clientId) {
  try {
    localStorage.setItem(dismissKey(slot, clientId), String(Date.now()));
  } catch (_) {
    // ignore
  }
}

// --- Service detection from contracts only ---
// We ignore equipment types on purpose: having a printer (systeme_impression)
// doesn't automatically mean the client has an MPS contract with us, and
// having a telephony equipment doesn't mean he's on Callwork. The contract
// type_service is the only reliable signal.
function detectServices(contracts) {
  const has = { mps: false, ucaas: false, itaas: false, draas: false };

  for (const c of contracts || []) {
    const ts = (c.type_service || '').toLowerCase();
    if (!ts) continue;
    if (ts.includes('mps') || ts.includes('psp')) has.mps = true;
    if (ts.includes('ucaas')) has.ucaas = true;
    if (ts.includes('itaas')) has.itaas = true;
    if (ts.includes('draas')) has.draas = true;
  }

  return has;
}

// --- Individual ad cards ---
const AdCard = ({
  icon: Icon,
  title,
  subtitle,
  description,
  ctaLabel,
  ctaLink,
  image,
  logo,
  gradientFrom,
  gradientTo,
  accentColor,
  testid,
  onDismiss,
  size = 'lg',
  darkText = false,
}) => {
  const [imgError, setImgError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const showImage = image && !imgError;
  const showLogo = logo && !logoError;
  const isCompact = size === 'sm';
  // Text colors adapt to background brightness
  const titleColor = darkText ? 'text-slate-900' : 'text-white';
  const bodyColor = darkText ? 'text-slate-600' : 'text-white/85';
  const subtitleColor = darkText ? 'text-slate-500' : 'text-white/80';
  const dismissBg = darkText ? 'bg-black/10 hover:bg-black/20 text-slate-700' : 'bg-black/30 hover:bg-black/50 text-white';

  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300 group ${
        isCompact ? 'min-h-[160px]' : 'min-h-[180px]'
      }`}
      style={{
        background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* Decorative blurred orb for depth */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: 'white' }}
      />

      {/* Dismiss button — primary ad only */}
      {onDismiss && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          aria-label="Masquer cette publicité"
          className={`absolute top-3 right-3 z-20 w-8 h-8 rounded-full ${dismissBg} flex items-center justify-center transition cursor-pointer`}
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div className="relative flex flex-col md:flex-row items-stretch h-full">
        <div
          className={`flex-1 flex flex-col justify-center ${titleColor} ${
            isCompact ? 'p-5 space-y-3' : 'p-5 md:p-6 lg:p-8 space-y-4 md:space-y-4'
          }`}
        >
          {/* Logo inline + subtitle in uppercase — no icon */}
          <div className={`flex items-center gap-2.5 text-[11px] uppercase font-semibold tracking-wider ${subtitleColor} ${onDismiss ? 'pr-10' : ''}`}>
            {showLogo && (
              <img
                src={logo}
                alt=""
                onError={() => setLogoError(true)}
                className={`object-contain shrink-0 ${
                  isCompact ? 'h-6 md:h-7' : 'h-7 md:h-10'
                }`}
              />
            )}
            <span className="truncate">{subtitle}</span>
          </div>
          <h3
            className={`font-bold leading-tight ${titleColor} ${
              isCompact ? 'text-lg' : 'text-xl md:text-xl lg:text-2xl'
            }`}
            style={{ fontFamily: 'Manrope, Inter, system-ui, sans-serif' }}
          >
            {title}
          </h3>
          {description && (
            <p className={`${bodyColor} leading-snug ${isCompact ? 'text-sm' : 'text-sm md:text-sm lg:text-base'}`}>
              {description}
            </p>
          )}
          <div className="pt-2 md:pt-1">
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 w-full md:w-auto justify-center md:justify-start px-6 py-3 md:px-5 md:py-2.5 rounded-xl text-base md:text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
              style={darkText
                ? { background: accentColor, color: '#fff', fontFamily: 'Manrope, Inter, system-ui, sans-serif' }
                : { background: '#fff', color: accentColor, fontFamily: 'Manrope, Inter, system-ui, sans-serif' }}
            >
              {ctaLabel}
              <ArrowRight className="w-5 h-5 md:w-4 md:h-4" />
            </a>
          </div>
        </div>

        {/* Product image — stacks below text on mobile, right column on desktop */}
        <div
          className={`relative flex items-center justify-center p-4 ${
            isCompact
              ? 'w-full md:w-48 h-40 md:h-auto'
              : 'w-full md:w-72 lg:w-80 h-48 md:h-auto'
          }`}
        >
          {showImage ? (
            <img
              src={image}
              alt=""
              className="max-w-[70%] md:max-w-full max-h-full object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-300"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={`w-24 h-24 rounded-2xl flex items-center justify-center backdrop-blur-sm ${
              darkText ? 'bg-slate-900/5' : 'bg-white/10'
            }`}>
              <Icon className={`w-12 h-12 ${darkText ? 'text-slate-400' : 'text-white/70'}`} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Ad templates (built with dynamic image+logo URLs from the admin library) ---
function buildAds(slots, { onDismissPrimary, primaryKey } = {}) {
  const dismissFor = (key) => (primaryKey === key ? onDismissPrimary : undefined);
  return {
    telephony: (
      <AdCard
        testid="ad-callwork"
        icon={Phone}
        subtitle="Téléphonie cloud"
        title="Passez à la téléphonie d'entreprise nouvelle génération"
        description="Standard intelligent, visioconférence, appels illimités fixes & mobiles — tout inclus, sans engagement matériel."
        ctaLabel="Demander une étude gratuite"
        ctaLink={CTA.telephony}
        image={resolveAsset(slots, 'callwork', 'image')}
        logo={resolveAsset(slots, 'callwork', 'logo')}
        gradientFrom="#F7F8FF"
        gradientTo="#FCF5EA"
        accentColor="#02a96d"
        darkText
        onDismiss={dismissFor('telephony')}
      />
    ),
    printing: (
      <AdCard
        testid="ad-kopier"
        icon={Printer}
        subtitle="Parc d'impression managé"
        title="Modernisez votre parc d'impression"
        description="Photocopieurs connectés, supervision compteurs, consommables inclus, facturation au compteur. Simplicité totale."
        ctaLabel="Obtenir une offre personnalisée"
        ctaLink={CTA.printing}
        image={resolveAsset(slots, 'kopier', 'image')}
        logo={resolveAsset(slots, 'kopier', 'logo')}
        gradientFrom="#5271ff"
        gradientTo="#1e1f4a"
        accentColor="#5271ff"
        onDismiss={dismissFor('printing')}
      />
    ),
    itaas: (
      <AdCard
        testid="ad-itaas"
        icon={Laptop}
        subtitle="Maintenance informatique ITaaS"
        title="Votre parc informatique infogéré"
        description="Postes, serveurs, réseau — supervision 24/7, support illimité, renouvellement matériel inclus."
        ctaLabel="Demander un audit gratuit"
        ctaLink={CTA.itaas}
        image={resolveAsset(slots, 'macbook-pro', 'image')}
        logo={resolveAsset(slots, 'macbook-pro', 'logo')}
        gradientFrom="#09535d"
        gradientTo="#064049"
        accentColor="#064049"
        onDismiss={dismissFor('itaas')}
      />
    ),
    draas: (
      <AdCard
        testid="ad-draas"
        icon={HardDrive}
        subtitle="Sauvegarde de données DRaaS"
        title="Protégez vos données avec la double sauvegarde"
        description="NAS Synology local + cloud chiffré. Reprise d'activité garantie après sinistre."
        ctaLabel="Étudier ma sauvegarde"
        ctaLink={CTA.draas}
        image={resolveAsset(slots, 'synology-nas', 'image')}
        logo={resolveAsset(slots, 'synology-nas', 'logo')}
        gradientFrom="#262626"
        gradientTo="#f97316"
        accentColor="#f97316"
        onDismiss={dismissFor('draas')}
      />
    ),
  };
}

// Compact ads for the secondary placement (no dismiss button, smaller layout)
function buildCompactAds(slots) {
  const ads = buildAds(slots);
  // Re-render each AdCard with size="sm" by cloning props
  return Object.fromEntries(
    Object.entries(ads).map(([key, el]) => [key, React.cloneElement(el, { size: 'sm' })])
  );
}

// --- Component ---
export const SmartAds = ({ clientId, placement = 'dashboard-main', className = '' }) => {
  const [contracts, setContracts] = useState([]);
  const [librarySlots, setLibrarySlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [dismissedSlots, setDismissedSlots] = useState({});

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      portalApi.get(`/client-portal/clients/${clientId}/contracts`).catch(() => ({ data: { contracts: [] } })),
      portalApi.get('/settings/ads/library').catch(() => ({ data: { slots: {} } })),
    ]).then(([cr, lib]) => {
      if (cancelled) return;
      setContracts(cr.data?.contracts || []);
      setLibrarySlots(lib.data?.slots || {});
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [clientId]);

  const eligibleSlots = useMemo(() => {
    const has = detectServices(contracts);
    // Simple rule: show an ad for every service the client doesn't have.
    // Order below is the display priority (main banner = first eligible).
    const eligible = [];
    if (!has.mps) eligible.push('printing');     // Kopier
    if (!has.ucaas) eligible.push('telephony');  // Callwork
    if (!has.itaas) eligible.push('itaas');      // Leaser
    if (!has.draas) eligible.push('draas');      // DRaaS
    return eligible;
  }, [contracts]);

  if (loading || !eligibleSlots.length) return null;

  // Primary ad (no dismiss — the banner stays visible)
  if (placement === 'dashboard-main') {
    const primary = eligibleSlots[0];
    if (!primary) return null;

    const ads = buildAds(librarySlots);
    return (
      <div className={className} data-testid="smart-ads-main">
        {ads[primary]}
      </div>
    );
  }

  // Secondary ads (bottom of dashboard) — compact, no dismiss
  if (placement === 'dashboard-secondary') {
    const rest = eligibleSlots.slice(1);
    if (!rest.length) return null;
    const ads = buildCompactAds(librarySlots);
    return (
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${className}`} data-testid="smart-ads-secondary">
        {rest.slice(0, 2).map((key) => (
          <React.Fragment key={key}>{ads[key]}</React.Fragment>
        ))}
      </div>
    );
  }

  return null;
};

export default SmartAds;
